import { useCallback, useEffect, useState } from 'react';
import { SdkErrorResponse } from 'src/utilities/notifications';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';

import { IDiner, SHORT_SEARCH_DELAY } from 'src/utilities/useSearchHooks';
import { IErrorResponse } from 'src/utilities/shared-types';
import { deactivateDiner } from 'src/common/features/DinersManagement/functions/deactivateDiner';

export type UseDeactivateDinerProps = {
  onSuccess: (props: DeactivateDinerFunctionProps) => void;
  onError: (error?: IErrorResponse | SdkErrorResponse) => void;
};

export type UseDeactivateDinerReturn = {
  deactivate: (props: DeactivateDinerFunctionProps) => Promise<void>;
  isLoading: boolean;
};

export type DeactivateDinerFunctionProps = {
  corpId: string;
  diner: IDiner;
};

export const useDeactivateDiner = ({
  onSuccess,
  onError,
}: UseDeactivateDinerProps): UseDeactivateDinerReturn => {
  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [requestProps, setRequestProps] =
    useState<DeactivateDinerFunctionProps | null>(null);

  const resetState = () => {
    setIsLoading(false);
    setRequestProps(null);
  };

  const handleError = (error?: IErrorResponse | SdkErrorResponse) => {
    onError(error);
    resetState();
  };

  const deactivate = useCallback<
    (props: DeactivateDinerFunctionProps) => Promise<void>
  >(
    async (props: DeactivateDinerFunctionProps) => {
      if (isLoading) {
        return;
      }

      try {
        setIsLoading(true);
        setRequestProps(props);

        await startAsyncTask(() =>
          deactivateDiner(props.corpId, props.diner.id)
        );
      } catch (error) {
        handleError(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading]
  );

  const handleSuccess = async () => {
    resetState();
    onSuccess(requestProps);
  };

  useEffect(() => {
    switch (asyncTaskStatus) {
      case AsyncTaskStatus.COMPLETE:
        /**
         * The reason for this timeout is that elastic search needs some time to rearrange the indexes
         * after a record is deleted. Without this timeout the search could potentially
         * return outdated results
         */
        setTimeout(() => {
          handleSuccess();
        }, SHORT_SEARCH_DELAY);
        break;

      case AsyncTaskStatus.ERROR:
        handleError();
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncTaskStatus]);

  return {
    deactivate,
    isLoading,
  };
};
